import { useEffect } from 'react';
import { usePublicConfig } from '@/hooks/usePublicConfig';

// Security functions for URL validation and CSS escaping
function isValidFontUrl(url: string): boolean {
  try {
    const parsedUrl = new URL(url);
    // Only allow HTTPS URLs for security
    if (parsedUrl.protocol !== 'https:') return false;
    
    // Check for valid font file extensions
    const validExtensions = ['.woff', '.woff2', '.ttf', '.otf', '.eot'];
    const hasValidExtension = validExtensions.some(ext => 
      parsedUrl.pathname.toLowerCase().endsWith(ext)
    );
    
    return hasValidExtension;
  } catch {
    return false;
  }
}

function escapeCSS(str: string): string {
  // Escape quotes and other potential CSS injection characters
  return str
    .replace(/\\/g, '\\\\')
    .replace(/'/g, "\\'")
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\A ')
    .replace(/\r/g, '\\D ');
}

export function usePublicTypography() {
  const { config } = usePublicConfig();

  useEffect(() => {
    if (!config) return;

    const { typography_source, typography_google_font, typography_custom_font_url } = config;
    console.log(`🎨 [usePublicTypography] Applying public typography - Source: ${typography_source}, Font: ${typography_google_font || typography_custom_font_url}`);

    // Remove existing font links
    const existingFontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"], link[href*="fonts/"]');
    existingFontLinks.forEach(link => {
      console.log('🗑️ [usePublicTypography] Removing existing font link:', link.getAttribute('href'));
      link.remove();
    });

    // Remove existing font styles
    const existingFontStyle = document.getElementById('custom-font-style');
    if (existingFontStyle) {
      console.log('🗑️ [usePublicTypography] Removing existing custom font style');
      existingFontStyle.remove();
    }

    if (typography_source === 'google' && typography_google_font) {
      // Load Google Font with proper preload attributes
      const fontUrl = `https://fonts.googleapis.com/css2?family=${typography_google_font.replace(' ', '+')}:wght@300;400;500;600;700&display=swap`;
      console.log('📦 [usePublicTypography] Loading Google Font:', fontUrl);
      
      const link = document.createElement('link');
      link.href = fontUrl;
      link.rel = 'stylesheet';
      link.crossOrigin = 'anonymous'; // Add for proper CORS handling
      document.head.appendChild(link);

      // Apply Google Font to document
      const fontFamily = `"${typography_google_font}", system-ui, sans-serif`;
      document.documentElement.style.setProperty('--font-family', fontFamily);
      document.body.style.fontFamily = fontFamily;
      console.log('✅ [usePublicTypography] Applied Google Font to document:', fontFamily);
      
    } else if (typography_source === 'custom' && typography_custom_font_url) {
      // Validate and sanitize custom font URL for security
      if (!isValidFontUrl(typography_custom_font_url)) {
        console.error('🚫 [usePublicTypography] Invalid custom font URL detected:', typography_custom_font_url);
        return;
      }
      
      // Load custom font with sanitized URL
      console.log('📦 [usePublicTypography] Loading custom font:', typography_custom_font_url);
      
      const style = document.createElement('style');
      style.id = 'custom-font-style';
      // Escape the URL to prevent CSS injection
      const escapedUrl = escapeCSS(typography_custom_font_url);
      style.innerHTML = `
        @font-face {
          font-family: 'CustomFont';
          src: url('${escapedUrl}') format('woff2'),
               url('${escapedUrl}') format('truetype');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);

      // Apply custom font to document
      const fontFamily = '"CustomFont", system-ui, sans-serif';
      document.documentElement.style.setProperty('--font-family', fontFamily);
      document.body.style.fontFamily = fontFamily;
      console.log('✅ [usePublicTypography] Applied custom font to document:', fontFamily);
    }
  }, [config?.typography_source, config?.typography_google_font, config?.typography_custom_font_url]);

  return {
    source: config?.typography_source || 'google',
    googleFont: config?.typography_google_font || 'Inter',
    customFontUrl: config?.typography_custom_font_url || null
  };
}